/**
 * AffordabilityPrefsCard
 *
 * Lives on /mortgage-calculator for signed-in users. Lets the visitor
 * tell the system how much they can actually put down, so the per-card
 * "est. $X/mo" line everywhere else uses *their* assumptions instead of
 * the generic 20% default.
 *
 * Three modes:
 *   percentage: down = price × X%
 *   capped:     down = min(price × X%, $Y) — prefer the %, but cap at $Y
 *   fixed:      down = $Y always — same dollar amount regardless of price
 *
 * Saving hits PUT /api/auth/me/affordability-prefs which also returns
 * the canonical row so we can echo it back into Redux via setUser.
 */
import { useEffect, useState } from 'react';
import { Loader2, PiggyBank } from 'lucide-react';
import toast from 'react-hot-toast';
import { apiClient } from '@/shared/api';
import { useAppDispatch } from '@/store/hooks';
import { setUser } from '@/store/slices';
import { useAuth } from '@/app/providers';
import type { DownPaymentMode } from '@/utils';

interface PrefsResponse {
    success: boolean;
    data: {
        down_payment_mode: DownPaymentMode | null;
        preferred_down_percentage: number | null;
        max_down_amount: number | null;
    };
}

export function AffordabilityPrefsCard() {
    const { user } = useAuth();
    const dispatch = useAppDispatch();

    const [mode, setMode] = useState<DownPaymentMode | ''>('');
    const [percent, setPercent] = useState<string>('');
    const [maxAmount, setMaxAmount] = useState<string>('');
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        setMode(user?.down_payment_mode ?? '');
        setPercent(
            user?.preferred_down_percentage !== null && user?.preferred_down_percentage !== undefined
                ? String(user.preferred_down_percentage)
                : '',
        );
        setMaxAmount(
            user?.max_down_amount !== null && user?.max_down_amount !== undefined
                ? String(user.max_down_amount)
                : '',
        );
    }, [user?.down_payment_mode, user?.preferred_down_percentage, user?.max_down_amount]);

    const needsPercent = mode === 'percentage' || mode === 'capped';
    const needsAmount = mode === 'capped' || mode === 'fixed';

    const handleSave = async (e: React.FormEvent) => {
        e.preventDefault();
        setSaving(true);
        try {
            const body = {
                down_payment_mode: mode === '' ? null : mode,
                preferred_down_percentage: needsPercent && percent ? Number(percent) : null,
                max_down_amount: needsAmount && maxAmount ? Number(maxAmount) : null,
            };
            const resp = await apiClient.put<PrefsResponse>('/auth/me/affordability-prefs', body);
            const data = resp.data?.data;
            if (data && user) {
                dispatch(setUser({
                    ...user,
                    down_payment_mode: data.down_payment_mode,
                    preferred_down_percentage: data.preferred_down_percentage,
                    max_down_amount: data.max_down_amount,
                }));
            }
            toast.success('Affordability preferences saved.');
        } catch (err: unknown) {
            const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail
                || 'Could not save preferences.';
            toast.error(detail);
        } finally {
            setSaving(false);
        }
    };

    return (
        <form onSubmit={handleSave} className="bg-white rounded-xl shadow-sm border p-5 space-y-4">
            <div className="flex items-start gap-3">
                <div className="p-2 bg-primary/10 rounded-lg shrink-0">
                    <PiggyBank size={20} className="text-primary-dark" />
                </div>
                <div className="min-w-0">
                    <h2 className="text-base font-semibold text-gray-900">My affordability</h2>
                    <p className="text-xs text-gray-500 mt-0.5">
                        Tells the listings page how to estimate your monthly payment.
                        Leave on default to use 20% down.
                    </p>
                </div>
            </div>

            <fieldset className="space-y-2">
                <ModeOption
                    name="down-mode"
                    value=""
                    current={mode}
                    onChange={(v) => setMode(v as DownPaymentMode | '')}
                    title="Use the default"
                    desc="20% down, 30-year term, current rate."
                />
                <ModeOption
                    name="down-mode"
                    value="percentage"
                    current={mode}
                    onChange={(v) => setMode(v as DownPaymentMode | '')}
                    title="A percentage of the home price"
                    desc='Always put down this %. Example: "I always put down 20%."'
                />
                <ModeOption
                    name="down-mode"
                    value="capped"
                    current={mode}
                    onChange={(v) => setMode(v as DownPaymentMode | '')}
                    title="Prefer a percentage, capped at a dollar amount"
                    desc='Example: "I prefer 20% down but never more than $100,000."'
                />
                <ModeOption
                    name="down-mode"
                    value="fixed"
                    current={mode}
                    onChange={(v) => setMode(v as DownPaymentMode | '')}
                    title="A fixed dollar amount"
                    desc='Example: "I always put down exactly $100,000 regardless of price."'
                />
            </fieldset>

            {(needsPercent || needsAmount) && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2 border-t border-gray-100">
                    {needsPercent && (
                        <label className="block">
                            <span className="block text-[11px] font-medium text-gray-600 mb-1">
                                Preferred % down
                            </span>
                            <div className="relative">
                                <input
                                    type="number"
                                    min={0}
                                    max={100}
                                    step={0.5}
                                    value={percent}
                                    onChange={(e) => setPercent(e.target.value)}
                                    placeholder="20"
                                    required
                                    className="w-full rounded-md border border-gray-300 px-2 py-1.5 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
                                />
                                <span className="absolute inset-y-0 right-2 flex items-center text-xs text-gray-400">%</span>
                            </div>
                        </label>
                    )}
                    {needsAmount && (
                        <label className="block">
                            <span className="block text-[11px] font-medium text-gray-600 mb-1">
                                {mode === 'capped' ? 'Maximum dollar amount' : 'Fixed dollar amount'}
                            </span>
                            <div className="relative">
                                <span className="absolute inset-y-0 left-2 flex items-center text-xs text-gray-400">$</span>
                                <input
                                    type="number"
                                    min={0}
                                    step={1000}
                                    value={maxAmount}
                                    onChange={(e) => setMaxAmount(e.target.value)}
                                    placeholder="100000"
                                    required
                                    className="w-full rounded-md border border-gray-300 pl-5 pr-2 py-1.5 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
                                />
                            </div>
                        </label>
                    )}
                </div>
            )}

            <div className="flex justify-end pt-1">
                <button
                    type="submit"
                    disabled={saving}
                    className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md bg-primary text-white text-sm font-semibold hover:bg-primary-dark disabled:opacity-50"
                >
                    {saving && <Loader2 size={14} className="animate-spin" />}
                    Save preferences
                </button>
            </div>
        </form>
    );
}

function ModeOption({
    name, value, current, onChange, title, desc,
}: {
    name: string;
    value: string;
    current: string;
    onChange: (v: string) => void;
    title: string;
    desc: string;
}) {
    const isActive = current === value;
    return (
        <label
            className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition ${
                isActive
                    ? 'border-primary bg-primary/5'
                    : 'border-gray-200 hover:border-gray-300'
            }`}
        >
            <input
                type="radio"
                name={name}
                value={value}
                checked={isActive}
                onChange={() => onChange(value)}
                className="mt-1 h-4 w-4 text-primary focus:ring-primary"
            />
            <div className="min-w-0">
                <p className="text-sm font-medium text-gray-900">{title}</p>
                <p className="text-xs text-gray-500 leading-snug">{desc}</p>
            </div>
        </label>
    );
}

export default AffordabilityPrefsCard;
